import {
    type SavedItinerariesState,
    type SavedItineraryEntry,
} from "../store/persistence/slices/savedItineraries.slice";
import type {
    SavedItineraryEntryDTO,
    SavedItineraryUpsertEntryDTO,
} from "../types/dtos/api/saved-itineraries/saved-itinerary.dto";
import { SAVED_ITINERARIES_MAX_ITEMS } from "./savedItinerary.tools";

/** Itinerary id (`itinerary.id`) — deduplication key shared with the backend `(userId, itineraryId)` unique key. */
const itineraryIdOf = ( entry: SavedItineraryEntry ): string => {
    return entry.itinerary.id;
};

/**
 * Sorts saved itinerary entries newest `savedAt` first and enforces the local cap.
 *
 * @param entries Entries to order.
 * @returns Ordered entries, truncated to {@link SAVED_ITINERARIES_MAX_ITEMS}.
 */
const sortAndCapEntries = ( entries: SavedItineraryEntry[] ): SavedItinerariesState => {
    return [ ...entries ].sort( ( a, b ) => b.savedAt - a.savedAt ).
        slice( 0, SAVED_ITINERARIES_MAX_ITEMS );
};

/**
 * Maps a local saved itinerary entry to its server-safe upsert payload.
 *
 * @remarks `localOnly` is client metadata and must never cross the API boundary.
 *
 * @param entry Local saved itinerary entry.
 * @returns Upsert payload accepted by `PUT /api/saved-itineraries`.
 */
export const toSavedItineraryUpsertDTO = ( entry: SavedItineraryEntry ): SavedItineraryUpsertEntryDTO => {
    return {
        "itinerary": entry.itinerary,
        "savedAt":   new Date( entry.savedAt ).toISOString(),
        ...entry.originLabel !== undefined ? { "originLabel": entry.originLabel } : {},
        ...entry.destinationLabel !== undefined ? { "destinationLabel": entry.destinationLabel } : {},
    };
};

/**
 * Maps a server saved-itinerary entry to its local persisted form.
 *
 * @param entry Entry returned by `GET`/`PUT /api/saved-itineraries`.
 * @returns Local saved itinerary entry (never `localOnly`).
 */
const fromSavedItineraryEntryDTO = ( entry: SavedItineraryEntryDTO ): SavedItineraryEntry => {
    const savedAt = Date.parse( entry.savedAt );

    return {
        "itinerary": entry.itinerary,
        "savedAt":   Number.isNaN( savedAt ) ? 0 : savedAt,
        ...entry.originLabel !== undefined ? { "originLabel": entry.originLabel } : {},
        ...entry.destinationLabel !== undefined ? { "destinationLabel": entry.destinationLabel } : {},
    };
};

/**
 * Rebuilds the local saved-itinerary state from the authoritative server list.
 *
 * @param entries Entries returned by `GET`/`PUT /api/saved-itineraries`.
 * @returns Ordered local state (newest first, capped).
 */
export const buildSavedEntriesFromServer = ( entries: SavedItineraryEntryDTO[] ): SavedItinerariesState => {
    return sortAndCapEntries( entries.map( fromSavedItineraryEntryDTO ) );
};

/**
 * Returns the local entries whose itinerary id is absent from the server list.
 *
 * These are browser-local favorites detected at login — candidates to merge
 * into the authenticated profile (or to flag `localOnly`).
 *
 * @param localState Current local saved itineraries.
 * @param serverEntries Authoritative server entries.
 * @returns Local entries unknown to the profile.
 */
export const findLocalOnlyExtras = (
    localState: SavedItinerariesState,
    serverEntries: SavedItineraryEntryDTO[],
): SavedItineraryEntry[] => {
    const serverIds = new Set( serverEntries.map( ( entry ) => entry.itineraryId ) );

    return localState.filter( ( entry ) => !serverIds.has( itineraryIdOf( entry ) ) );
};

/**
 * Flags the given itinerary ids as `localOnly` in the state.
 *
 * @param state Current saved itineraries.
 * @param itineraryIds Itinerary ids to mark.
 * @returns Updated state with matching entries flagged.
 */
export const markEntriesLocalOnly = (
    state: SavedItinerariesState,
    itineraryIds: ReadonlySet<string>,
): SavedItinerariesState => {
    return state.map( ( entry ) => {
        return itineraryIds.has( itineraryIdOf( entry ) )
            ? {
                ...entry,
                "localOnly": true,
            }
            : entry;
    } );
};

/**
 * Clears the `localOnly` flag of the given itinerary ids — typically after a
 * successful push to the server profile.
 *
 * @param state Current saved itineraries.
 * @param itineraryIds Itinerary ids to unflag.
 * @returns Updated state with matching entries unflagged.
 */
export const clearLocalOnlyFlag = (
    state: SavedItinerariesState,
    itineraryIds: ReadonlySet<string>,
): SavedItinerariesState => {
    return state.map( ( entry ) => {
        if ( !itineraryIds.has( itineraryIdOf( entry ) ) || entry.localOnly !== true ) {
            return entry;
        }

        const next: SavedItineraryEntry = { ...entry };
        delete next.localOnly;

        return next;
    } );
};

/**
 * Merges the authoritative server list with flagged local-only extras.
 *
 * Server entries win on id collisions and never carry `localOnly`; extras are
 * kept as-is. The result is ordered newest-first and capped.
 *
 * @param serverEntries Authoritative server entries.
 * @param localExtras Entries present locally but absent from the profile.
 * @returns Merged local state.
 */
export const mergeServerAndLocalExtras = (
    serverEntries: SavedItineraryEntryDTO[],
    localExtras: SavedItineraryEntry[],
): SavedItinerariesState => {
    const serverIds = new Set( serverEntries.map( ( entry ) => entry.itineraryId ) );
    const merged = [
        ...serverEntries.map( fromSavedItineraryEntryDTO ),
        ...localExtras.filter( ( entry ) => !serverIds.has( itineraryIdOf( entry ) ) ),
    ];

    return sortAndCapEntries( merged );
};

/**
 * Keeps only the `localOnly` entries — the logout purge that drops profile-synced
 * favorites while preserving device-local ones.
 *
 * @param state Current saved itineraries.
 * @returns State reduced to local-only entries.
 */
export const keepLocalOnlyEntries = ( state: SavedItinerariesState ): SavedItinerariesState => {
    return state.filter( ( entry ) => entry.localOnly === true );
};

/** Auth-transition kinds relevant to the saved-itineraries sync bootstrap. */
export type SavedItinerariesAuthTrigger =
  | "bootAnonymous" |
  "bootAuthenticated" |
  "login" |
  "logout" |
  "unchanged";

/**
 * Classifies an access-token change for the saved-itineraries bootstrap.
 *
 * Mirrors `PurchasedItinerariesBootstrap` semantics: `previous === undefined`
 * marks the first hydrated read (boot), later reads compare auth presence only
 * — a token value change while staying logged in is not a transition.
 *
 * @param previousToken Previous token value (`undefined` until the first hydrated read).
 * @param accessToken Current access token.
 * @returns The transition kind driving sync and merge-prompt decisions.
 */
export const resolveSavedItinerariesAuthTrigger = (
    previousToken: string | null | undefined,
    accessToken: string | null,
): SavedItinerariesAuthTrigger => {
    const isAuthenticated = accessToken != null && accessToken !== "";
    const wasAuthenticated = previousToken != null && previousToken !== "";

    if ( previousToken === undefined ) {
        return isAuthenticated ? "bootAuthenticated" : "bootAnonymous";
    }
    if ( isAuthenticated === wasAuthenticated ) {
        return "unchanged";
    }

    return isAuthenticated ? "login" : "logout";
};
